import React, { useEffect, useMemo, useRef, useState } from 'react';
import './campaign-card.css';
import queryString from 'query-string';
import CampaignCard from './CampaignCard';
import CuratorOfferSummary from '../../components/Campaigns/CuratorOfferSummary';
import { AddToPlayListModal } from '../../components/AddToPlayListModal';
import { useAccountCuratorOffer } from '../../hooks/useAccountCuratorOffer';
import InstantShareModal from '../../components/ShareModal/InstantShareModal';
import DeleteAndFixModal from '../../components/DeleteAndFixModal/DeleteAndFixModal';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import SectionWrapperNew from '../../components/SectionWrapper/SectionWrapperNew';
import { Link, useLocation } from 'react-router-dom';
import { getCurrentUserProfileObjectId } from '../../utils';
import { CampaignsPageStyle, CampaignsHeader, CampaignsContent } from './CampaignsPage.style';
import { isSpotifyConnectedForCurator } from '../../utils/curatorOffer';
import {
  CAMPAIGNS_ONBOARDING_PATH,
  isOnboardingIntentFeatureEnabled,
  requiresCampaignsOnboarding,
  setPostSpotifyRedirect,
} from '../../utils/onboardingIntent';
import { getAuthAccountUserId } from '../../utils/authAccount';
import {
  fetchAccountCampaigns,
  markAccountCampaignsSeen,
  rejectAllocation,
  rejectCampaign,
} from '../../services/campaignSubmissionService';
import {
  filterCampaignsToOpenOfferProfiles,
  resolveActingProfileId,
  shouldShowCampaignsForActiveProfile,
} from '../../utils/campaignAcceptPlaylists';
import { getCuratorAllocation, type CuratorAllocation } from '../../utils/campaignAllocations';
import { syncEmailInvitesForCurator } from '../../utils/syncEmailInvitesForCurator';

// Defining allocation types for split campaigns — re-exported shape from campaignAllocations util
interface Campaign {
  _id: string;
  artist_name: string;
  artist_email: string;
  budget: string;
  currencyCode?: string;
  campaign_type: string;
  created_at: string;
  curatorIds: string[];
  rejectedByIds: string[];
  acceptedById: string;
  end_date: string;
  spotify_link: string;
  spotify_uri: string;
  start_date: string;
  status: string;
  track_image: string;
  track_link: string;
  track_lyrics: any[];
  track_genre: any[];
  track_name: string;
  track_released: boolean;
  updated_at: string;
  userId: string;
  __v: number;
  curatorResponse: {
    endTime: string;
    [key: string]: any;
  };
  allocationMode?: 'single' | 'split';
  estimatedStreams?: number;
  offeredRatePer1k?: number;
  offeredPayout?: number;
  curatorAllocations?: CuratorAllocation[];
  assignedProfileIds?: string[];
}

const CampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [rejectingCampaign, setRejectingCampaign] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [songURI, setSongURI] = useState<string>('');
  // Add state for InstantShareModal
  const [instantShareSongModal, setInstantShareSongModal] = useState<boolean>(false);

  const [isRejectConfirmOpen, setIsRejectConfirmOpen] = useState<boolean>(false);
  const [campaignToReject, setCampaignToReject] = useState<string | null>(null);

  const location = useLocation();

  const detectCampaignsOnboarding = (): boolean => {
    if (!isOnboardingIntentFeatureEnabled()) return false;
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const params = new URLSearchParams(location.search || '');
      // URL onboarding mode always wins for first-time A/C landing
      if (params.get('onboarding') === '1') return true;
      // Explicitly completed — do not re-force from intent alone
      if (userInfo?.campaignsOnboardingRequired === false) {
        return false;
      }
      if (userInfo?.campaignsOnboardingRequired === true) return true;
      // Mid-flow recovery when flag missing but intent is A/C
      return requiresCampaignsOnboarding(
        userInfo?.onboardingIntent,
        userInfo?.campaignsOnboardingRequired
      );
    } catch {
      return false;
    }
  };

  const startSpotifyConnectForOnboarding = () => {
    setPostSpotifyRedirect(CAMPAIGNS_ONBOARDING_PATH);
    window.location.replace(
      'https://accounts.spotify.com/authorize?' +
        queryString.stringify({
          response_type: 'code',
          client_id: encodeURIComponent(process.env.REACT_APP_CLIENT_ID || ''),
          scope: 'playlist-modify-private playlist-modify-public playlist-read-private',
          redirect_uri: process.env.REACT_APP_REDIRECT_URI,
          show_dialog: false,
        })
    );
  };

  const [isCampaignsOnboarding, setIsCampaignsOnboarding] = useState<boolean>(false);

  // Use a ref to keep track of the last selected campaign URI to preserve it between modal reopens
  const lastSelectedURIRef = useRef<string>('');

  const {
    acceptCampaigns,
    accountHasOpenOffer,
    accountUserId,
    activeProfile,
    activeProfileAcceptsCampaigns,
    activeProfileOfferLabel,
    headerOffer,
    linkedProfiles,
    loadAccountCuratorState,
    offerLoading,
    setSpotifyGateMessage,
    spotifyGateMessage,
  } = useAccountCuratorOffer({
    currentUserId,
  });

  const activeProfileOpen = shouldShowCampaignsForActiveProfile(linkedProfiles, currentUserId);
  const visibleCampaigns = useMemo(() => {
    if (!activeProfileOpen) {
      return [];
    }

    return filterCampaignsToOpenOfferProfiles(campaigns, linkedProfiles, currentUserId);
  }, [activeProfileOpen, campaigns, linkedProfiles, currentUserId]);

  useEffect(() => {
    const initializeCampaignsPage = async () => {
      try {
        setLoading(true);

        // Check if user is logged in but doesn't have Spotify connection
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || '{}');
        const hasSpotifyConnection = localStorage.getItem('hasSpotifyConnection') === 'true';
        const onboardingActive = detectCampaignsOnboarding();
        setIsCampaignsOnboarding(onboardingActive);

        if (userInfo?.userId && !spotifyUser?.username && !hasSpotifyConnection) {
          // User is logged in but no Spotify connection
          if (onboardingActive) {
            // Guided empty state (not a hard error) — user must connect Spotify first
            setError(null);
            setSpotifyGateMessage(
              'Connect your Spotify account to finish setup and start receiving submissions.'
            );
            setLoading(false);
            return;
          }
          setError('Please connect Spotify first to use campaigns.');
          setLoading(false);
          return;
        }

        // Get the current UserProfile ObjectId
        const userProfileObjectId = await getCurrentUserProfileObjectId();

        if (userProfileObjectId) {
          setCurrentUserId(userProfileObjectId);

          // Convert any pending email-based invites into normal curator assignments
          // before marking campaigns seen or fetching the inbox.
          await syncEmailInvitesForCurator(userProfileObjectId);

          const authAccountUserId = getAuthAccountUserId();
          if (authAccountUserId) {
            await markAccountCampaignsSeen(authAccountUserId);
          }

          window.dispatchEvent(new CustomEvent('campaignsMarkedAsSeen'));

          await loadAccountCuratorState(userProfileObjectId);

          // Only fetch campaigns if user has enabled campaign acceptance
          // This will be handled after we get the user profile data
        } else {
          console.error('Could not get UserProfile ObjectId');
          setError('Could not identify current user profile. Please try logging out and back in.');
        }
      } catch (err) {
        console.error('Error initializing Campaigns Page or marking campaigns as seen:', err);
        setError('Failed to load campaigns or mark as seen. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    initializeCampaignsPage();
  }, []);

  useEffect(() => {
    const fetchCampaignsIfReady = async () => {
      if (!accountUserId || !linkedProfiles.length) return;

      try {
        await syncEmailInvitesForCurator(currentUserId || '');
        const { campaigns } = await fetchAccountCampaigns(accountUserId);
        setCampaigns(campaigns);
        setError(null);
      } catch (err) {
        console.error('Error fetching campaigns:', err);
        setError('Failed to load campaigns. Please try again later.');
      }
    };

    fetchCampaignsIfReady();
  }, [accountUserId, linkedProfiles, acceptCampaigns, currentUserId]);

  useEffect(() => {
    const syncActiveProfile = async () => {
      const profileId = await getCurrentUserProfileObjectId();
      if (profileId) {
        setCurrentUserId(profileId);
      }
    };

    syncActiveProfile();
    window.addEventListener('focus', syncActiveProfile);

    return () => {
      window.removeEventListener('focus', syncActiveProfile);
    };
  }, [linkedProfiles]);

  useEffect(() => {
    if (!currentUserId) return;

    const refreshCuratorState = () => {
      loadAccountCuratorState(currentUserId);
      if (accountUserId) {
        fetchAccountCampaigns(accountUserId)
          .then(({ campaigns }) => setCampaigns(campaigns))
          .catch(err => console.error('Error refreshing campaigns after offer update:', err));
      }
    };

    window.addEventListener('acceptCampaignsChanged', refreshCuratorState);
    return () => {
      window.removeEventListener('acceptCampaignsChanged', refreshCuratorState);
    };
  }, [accountUserId, currentUserId, loadAccountCuratorState]);

  useEffect(() => {
    if (!accountUserId) return;

    const intervalId = setInterval(() => {
      fetchAccountCampaigns(accountUserId)
        .then(({ campaigns }) => {
          setCampaigns(campaigns);
          setError(null);
        })
        .catch(err => {
          console.error('Error fetching campaigns in interval:', err);
        });
    }, 15000);

    return () => {
      clearInterval(intervalId);
    };
  }, [accountUserId, acceptCampaigns]);

  useEffect(() => {
    // Check for instantShareDetail in localStorage on component mount
    try {
      let instantShareDetail = JSON.parse(localStorage.getItem('instantShareDetail') || 'null');

      // Also check if we have the songAdded URL parameter
      const searchParams = new URLSearchParams(location.search);
      const songWasJustAdded = searchParams.get('songAdded') === 'true';

      if (instantShareDetail || songWasJustAdded) {
        setInstantShareSongModal(true);

        // If we came back from adding a song, clean up the URL
        if (songWasJustAdded) {
          // Remove the songAdded parameter from the URL without triggering a refresh
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
        }
      }
    } catch (error) {
      console.error('Error parsing instantShareDetail:', error);
    }
  }, [location.search]);

  const handleAccept = (id: string) => {
    const campaign = campaigns.find(campaign => campaign._id === id);
    if (campaign) {
      // Save the campaign data to state
      setSelectedCampaign(campaign);
      setSongURI(campaign.spotify_uri);
      lastSelectedURIRef.current = campaign.spotify_uri;

      // Show the modal
      setIsModalVisible(true);
    }
  };

  // Handle modal visibility
  const handleModalVisibility = (visible: boolean) => {
    setIsModalVisible(visible);

    // When the modal is reopening, ensure the songURI is reset from our ref
    if (visible && selectedCampaign) {
      setSongURI(lastSelectedURIRef.current);
    }
  };

  // When songURI changes, update our ref to keep track of it
  useEffect(() => {
    if (songURI) {
      lastSelectedURIRef.current = songURI;
    }
  }, [songURI]);

  const handleRejectClick = (id: string) => {
    // Show confirmation modal
    setCampaignToReject(id);
    setIsRejectConfirmOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!campaignToReject || !currentUserId) return;

    try {
      setRejectingCampaign(campaignToReject);
      setIsRejectConfirmOpen(false);

      const campaign = campaigns.find(c => c._id === campaignToReject);
      if (!campaign) {
        console.error('Campaign not found');
        setRejectingCampaign(null);
        setCampaignToReject(null);
        return;
      }

      const accountProfileIds = linkedProfiles.map(profile => profile.id);
      const actingProfileId = resolveActingProfileId(campaign, accountProfileIds, currentUserId);

      if (campaign.allocationMode === 'split') {
        const allocation = getCuratorAllocation(campaign, accountProfileIds);

        if (!allocation) {
          console.error('Allocation not found for curator');
          setRejectingCampaign(null);
          setCampaignToReject(null);
          return;
        }

        const response = await rejectAllocation({
          campaignId: campaignToReject,
          curatorId: actingProfileId,
          allocationId: allocation._id,
        });

        if (response.success) {
          // Update the local state to reflect the rejection
          setCampaigns(prevCampaigns =>
            prevCampaigns.map(c =>
              c._id === campaignToReject && c.curatorAllocations
                ? {
                    ...c,
                    curatorAllocations: c.curatorAllocations.map(alloc =>
                      alloc._id === allocation._id
                        ? {
                            ...alloc,
                            rejectedByIds: [...(alloc.rejectedByIds || []), actingProfileId],
                            // Only set status to rejected if all curators rejected
                            status: alloc.curatorIds.every(id =>
                              [...(alloc.rejectedByIds || []), actingProfileId].includes(id)
                            )
                              ? ('rejected' as const)
                              : alloc.status,
                          }
                        : alloc
                    ),
                    rejectedByIds: [...(c.rejectedByIds || []), actingProfileId],
                  }
                : c
            )
          );
        } else {
          console.error('Failed to reject allocation:', response.message);
        }
      } else {
        // Use existing reject endpoint for single mode campaigns
        const response = await rejectCampaign({
          userObjectId: actingProfileId,
          campaignId: campaignToReject,
        });

        if (response.success) {
          // Update the local state to reflect the rejection
          setCampaigns(prevCampaigns =>
            prevCampaigns.map(c =>
              c._id === campaignToReject
                ? {
                    ...c,
                    rejectedByIds: [...(c.rejectedByIds || []), actingProfileId],
                  }
                : c
            )
          );
        } else {
          console.error('Failed to reject campaign:', response.message);
        }
      }
    } catch (error) {
      console.error('Error rejecting campaign:', error);
    } finally {
      // Clear the rejecting state
      setRejectingCampaign(null);
      setCampaignToReject(null);
    }
  };

  const handleRejectCancel = () => {
    setIsRejectConfirmOpen(false);
    setCampaignToReject(null);
  };

  return (
    <CampaignsPageStyle>
      <CampaignsHeader>
        <div className="campaigns-header-content">
          <div className="campaigns-title-section">
            <h1 className="campaigns-page-title">Campaigns</h1>
            <p className="campaigns-page-subtitle">
              Manage the campaigns you have been selected for
            </p>
          </div>

          <div className="campaigns-controls-section">
            <div className="campaign-offer-panel">
              {loading || offerLoading ? (
                <LoadingSpinner text="Loading offer…" containerPadding="28px 20px" />
              ) : spotifyGateMessage ? (
                <div className="setup-step-card">
                  <div className="setup-step-card__icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                    </svg>
                  </div>
                  <div className="setup-step-card__body">
                    <p className="setup-step-card__title">Connect Spotify to continue</p>
                    <p className="setup-step-card__text">
                      {isCampaignsOnboarding && !isSpotifyConnectedForCurator()
                        ? 'Link your account to create your curator profile and start receiving submissions.'
                        : 'Connect Spotify from the header, then return here to finish setup.'}
                    </p>
                    {isCampaignsOnboarding && !isSpotifyConnectedForCurator() ? (
                      <button
                        type="button"
                        className="setup-step-card__spotify-btn"
                        onClick={startSpotifyConnectForOnboarding}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                        </svg>
                        Connect Spotify
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : headerOffer ? (
                <div className="offer-summary-card">
                  <div className="offer-summary-card__header">
                    <span>{activeProfileOfferLabel || 'Your current offer'}</span>
                  </div>
                  <CuratorOfferSummary
                    offer={headerOffer}
                    linkedProfiles={linkedProfiles}
                    activeProfileUsername={activeProfile?.username}
                    showActiveProfileOnly
                  />
                </div>
              ) : isSpotifyConnectedForCurator() ? (
                <div className="setup-step-card setup-step-card--offer">
                  <div className="setup-step-card__body">
                    <p className="setup-step-card__title">
                      {activeProfile?.username || 'This profile'} did not create an offer yet
                    </p>
                    <p className="setup-step-card__text">
                      {accountHasOpenOffer
                        ? 'Add this profile to one of your offers to start receiving campaigns.'
                        : 'Create and save an offer with this profile to start receiving campaigns from admins.'}
                    </p>
                    <Link to="/curator-offers" className="setup-step-card__offer-btn">
                      Go to My Offers
                    </Link>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </CampaignsHeader>

      <CampaignsContent>
        {loading ? (
          <LoadingSpinner text="Loading campaigns..." />
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : !activeProfileOpen ? (
          <div className="campaigns-disabled-message">
            <div className="disabled-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <h3>This profile did not create an offer yet</h3>
            <p>
              Switch to a Spotify profile on one of your offers, or{' '}
              <Link to="/curator-offers">add this profile on My Offers</Link>.
            </p>
          </div>
        ) : !activeProfileAcceptsCampaigns && visibleCampaigns.length === 0 ? (
          <div className="campaigns-disabled-message">
            <div className="disabled-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <h3>No campaigns yet</h3>
            <p>
              Add <strong>{activeProfile?.username || 'this profile'}</strong> to an offer on{' '}
              <Link to="/curator-offers">My Offers</Link> to receive new campaigns from admins.
            </p>
          </div>
        ) : (
          <div className="campaigns-section-wrapper">
            <SectionWrapperNew heading={'Available campaigns'} number={visibleCampaigns.length}>
              <div>
                {!activeProfileAcceptsCampaigns && visibleCampaigns.length > 0 && (
                  <div className="existing-campaigns-notice">
                    <div className="notice-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </div>
                    <p>
                      <strong>Showing your existing campaigns only.</strong> This profile is not on
                      an offer, so you will not receive new campaigns until you add it on{' '}
                      <Link to="/curator-offers">My Offers</Link>.
                    </p>
                  </div>
                )}
                {visibleCampaigns.length > 0 ? (
                  <div className="campaigns-list">
                    {visibleCampaigns.map(campaign => {
                      const accountProfileIds = currentUserId
                        ? [currentUserId]
                        : linkedProfiles.map(profile => profile.id);
                      const curatorAllocation = getCuratorAllocation(campaign, accountProfileIds);
                      return (
                        <CampaignCard
                          key={campaign._id}
                          _id={campaign._id}
                          track_name={campaign.track_name}
                          artist_name={campaign.artist_name}
                          spotify_link={campaign.spotify_link}
                          track_image={campaign.track_image}
                          track_released={campaign.track_released}
                          track_lyrics={campaign.track_lyrics}
                          track_genre={campaign.track_genre}
                          campaign_type={campaign.campaign_type}
                          start_date={campaign.start_date}
                          end_date={campaign.end_date}
                          budget={campaign.budget}
                          currencyCode={campaign.currencyCode}
                          status={campaign.status}
                          estimatedStreams={campaign.estimatedStreams}
                          offeredRatePer1k={campaign.offeredRatePer1k}
                          offeredPayout={campaign.offeredPayout}
                          rejectedByIds={campaign.rejectedByIds || []}
                          acceptedById={campaign.acceptedById || ''}
                          onAccept={() => handleAccept(campaign._id)}
                          onReject={() => handleRejectClick(campaign._id)}
                          currentUserId={currentUserId}
                          isRejecting={rejectingCampaign === campaign._id}
                          curatorResponse={campaign.curatorResponse}
                          allocationMode={campaign.allocationMode}
                          curatorAllocation={curatorAllocation}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-campaigns-message">
                    <h3>No campaigns found</h3>
                    <p>When campaigns become available, they will appear here.</p>
                  </div>
                )}
              </div>
            </SectionWrapperNew>
          </div>
        )}
      </CampaignsContent>

      {/* Add to Playlist Modal */}
      {selectedCampaign && (
        <AddToPlayListModal
          isModalVisible={isModalVisible}
          setIsModalVisible={handleModalVisibility}
          songURI={songURI}
          setSongURI={setSongURI}
          hideArtistAndSongName={selectedCampaign.track_released ? true : false}
          isFromCampaign={true}
          campaignData={selectedCampaign}
          linkedProfiles={linkedProfiles}
          accountUserId={accountUserId || undefined}
        />
      )}

      {/* Share Modal */}
      <InstantShareModal
        isModalVisible={instantShareSongModal}
        setIsModalVisible={setInstantShareSongModal}
        titleType="added from campaign"
      />

      {/* Reject Confirmation Modal */}
      <DeleteAndFixModal
        handleOk={handleRejectConfirm}
        handleCancel={handleRejectCancel}
        showModal={() => setIsRejectConfirmOpen(true)}
        isModalOpen={isRejectConfirmOpen}
        heading="Reject Campaign"
        headingText="Are you sure you want to reject this campaign? This action cannot be undone."
        button1="Reject"
        button2="Cancel"
      />
    </CampaignsPageStyle>
  );
};

export default CampaignsPage;
